import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/utils/cn';

// ============================================
// TYPES
// ============================================

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'link';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  asChild?: boolean;
}

// ============================================
// STYLES
// ============================================

const baseStyles = `
  inline-flex items-center justify-center whitespace-nowrap rounded-md
  font-medium transition-colors focus-visible:outline-none focus-visible:ring-2
  focus-visible:ring-brand-500 focus-visible:ring-offset-2
  disabled:pointer-events-none disabled:opacity-50
`;

const variantStyles = {
  default: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800',
  outline:
    'border border-[var(--border-primary)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] active:bg-[var(--bg-tertiary)]',
  ghost: 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] active:bg-[var(--bg-tertiary)]',
  destructive: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  link: 'text-brand-600 underline-offset-4 hover:underline',
};

const sizeStyles = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-10 w-10',
};

// ============================================
// COMPONENT
// ============================================

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'md', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
